import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardCheck, Loader2, Gauge, History, Settings2, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMessage, errFields } from "@/lib/api";
import { useAuth, isReadOnly } from "@/context/AuthContext";
import { PageTitle, Loading, ErrorBox, Empty, Section, InfoItem, FieldError } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { InspectionCard } from "@/components/Cards";
import { fmtDateTime } from "@/lib/time";
import { POLICY_LABEL, EQUIP_STATUS_OPTIONS, identLabel } from "@/lib/labels";
import { selectCls } from "@/pages/Take5New";
import { SharesSection, RIGHT_LABEL } from "@/components/SharesSection";

const StatusChange = ({ eq, onDone }) => {
  const [status, setStatus] = useState(eq.status);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/equipment/${eq.id}/status`, { status, reason });
      toast.success("État de l'équipement modifié.");
      setReason("");
      onDone(r.data);
    } catch (e) {
      setError(errFields(e).reason || errMessage(e));
    } finally { setBusy(false); }
  };
  return (
    <Section title="Changer l'état (administrateur)" icon={Settings2} testId="equipment-status-change">
      <div className="grid gap-3 sm:grid-cols-[220px_1fr_auto] sm:items-start">
        <select data-testid="equipment-status-select" className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          {EQUIP_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Textarea data-testid="equipment-status-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raison (obligatoire) : réparation effectuée, contrôle…" />
        <Button data-testid="equipment-status-submit" onClick={submit} disabled={busy} className="h-12">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Appliquer</Button>
      </div>
      <FieldError error={error} testId="equipment-status-error" />
    </Section>
  );
};

export default function EquipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eq, setEq] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  useEffect(() => { api.get("/teams/mine").then((r) => setTeams(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setError("");
    api.get(`/equipment/${id}`).then((r) => setEq(r.data)).catch((e) => setError(errMessage(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const inspect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inspections", { equipment_id: id, team_id: teamId || null });
      navigate(`/inspections/${data.id}`);
    } catch (e) { toast.error(errMessage(e)); setBusy(false); }
  };

  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!eq) return <Loading />;
  const draft = eq.inspections.find((i) => i.status === "brouillon" && i.user_id === user.id);
  const canInspect = !isReadOnly(user) && ["INSPECT", "MANAGE"].includes(eq.my_right);
  return (
    <div className="space-y-5">
      <PageTitle kicker={eq.code} title={eq.name} testId="equipment-detail-title">
        <StatusBadge status={eq.status} size="lg" testId="equipment-detail-status" />
      </PageTitle>
      {eq.status === "bloque" && <p data-testid="equipment-blocked-notice" role="alert" className="rounded-lg border-2 border-red-500 bg-red-950/60 p-3 text-sm font-semibold text-red-100">Équipement bloqué suite à une anomalie critique. Ne pas l'utiliser. Seul un administrateur peut lever le blocage.</p>}
      {!eq.is_owner && <p data-testid="equipment-shared-notice" className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm">Équipement partagé par <b>{eq.owner_name}</b> · votre droit : <b>{RIGHT_LABEL[eq.my_right]}</b>. Vos inspections restent visibles par le propriétaire.</p>}
      {canInspect && teams.length > 0 && (
        <select data-testid="inspection-team-select" aria-label="Équipe" className={selectCls} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Inspection sans équipe</option>
          {teams.map((t) => <option key={t.id} value={t.id}>Pour l'équipe {t.name}</option>)}
        </select>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {canInspect && <Button data-testid="start-inspection-button" onClick={inspect} disabled={busy} className="h-14 text-base font-bold">
          {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <ClipboardCheck className="mr-2 h-5 w-5" />} Inspecter cet équipement
        </Button>}
        {draft && <Button variant="outline" data-testid="resume-inspection-button" onClick={() => navigate(`/inspections/${draft.id}`)} className="h-14">Reprendre l'inspection en cours</Button>}
      </div>
      <Section title="Fiche" icon={Info} testId="equipment-info">
        <div className="grid gap-4 sm:grid-cols-3">
          <InfoItem label="Site" value={eq.site?.name} />
          <InfoItem label="Type" value={eq.type_name} />
          <InfoItem label="Modèle d'inspection" value={eq.template_name} />
          <InfoItem label="Compteur" value={eq.has_meter ? `Oui (${eq.meter_unit || "unité"}) · dernier relevé : ${eq.last_meter_value ?? "—"}` : "Non"} testId="equipment-meter-info" />
          <InfoItem label="Photos de prise en charge" value={POLICY_LABEL[eq.photo_policy]} />
          {(eq.identifiers || []).map((i) => <InfoItem key={i.id} label={identLabel(i)} value={<span className="font-data">{i.value}</span>} testId={`equipment-identifier-${i.kind}`} />)}
        </div>
      </Section>
      {!isReadOnly(user) && (eq.my_right === "MANAGE" || (eq.is_owner && user.role === "admin")) && <StatusChange eq={eq} onDone={(d) => setEq({ ...eq, ...d })} />}
      {eq.is_owner && user.role === "admin" && <SharesSection eq={eq} onDone={setEq} />}
      <Section title="Historique des inspections" icon={History} testId="equipment-history">
        {!eq.inspections.length && <Empty>Aucune inspection pour cet équipement.</Empty>}
        <div className="grid gap-3 md:grid-cols-2">{eq.inspections.map((i) => <InspectionCard key={i.id} i={{ ...i, equipment: { code: eq.code, name: eq.name }, site: eq.site }} showUser />)}</div>
      </Section>
      {eq.has_meter && (
        <Section title="Relevés du compteur" icon={Gauge} testId="equipment-meter-history">
          {!eq.meter_readings.length && <Empty>Aucun relevé.</Empty>}
          <ul className="divide-y divide-border">
            {eq.meter_readings.map((m) => (
              <li key={m.inspection_id} className="flex justify-between py-2 font-data text-sm"><span>{fmtDateTime(m.at, eq.site)} · {m.user_name}</span><span className="font-bold">{m.value} {eq.meter_unit}</span></li>
            ))}
          </ul>
        </Section>
      )}
      {eq.status_history.length > 0 && (
        <Section title="Journal de l'équipement" icon={History}>
          <ul className="space-y-2 text-sm">
            {eq.status_history.map((h) => (
              <li key={h.id} className="rounded-md border border-border p-2"><span className="font-data text-xs text-muted-foreground">{fmtDateTime(h.at, eq.site)} · {h.user_name}</span><br />
                {h.action === "equipment.block" ? "Blocage automatique (anomalie critique)"
                  : h.action === "equipment.status_change" ? `État : ${h.details.from} → ${h.details.to}`
                  : h.action === "admin.create" ? "Création de l'équipement"
                  : h.action === "equipment.share" ? `Partagé avec ${h.details.to} (${RIGHT_LABEL[h.details.right] || h.details.right})`
                  : h.action === "equipment.unshare" ? `Partage retiré : ${h.details.to}`
                  : `Modification : ${Object.keys(h.details?.changes || {}).join(", ") || "aucun changement"}`}{h.details?.reason ? ` — ${h.details.reason}` : ""}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
